import React, { useState } from 'react';
import posthog from 'posthog-js';
import useSectionView from '../../hooks/useSectionView';
import { PRICING } from '../../constants/pricing';

const ITEMS = [
  {
    q: 'Do I have to connect my bank account?',
    a: 'No. Right now you enter your income manually — takes about 30 seconds. We never ask for your bank login or account credentials.',
  },
  {
    q: 'How is this different from QuickBooks?',
    a: "QuickBooks is built for small businesses with employees and invoicing. TruFlow is built specifically for 1099 trades workers with seasonal income. We don't try to be accounting software. We answer one question well: what do you owe in taxes, and when.",
  },
  {
    q: "What if I haven't paid taxes in years?",
    a: "You're not alone — this comes up more than you'd expect. TruFlow shows you your current quarterly obligation, which is the first step. For older years, you'll want to talk to a CPA about a catch-up plan. We can show you the math; sorting out past years is between you and the IRS.",
  },
  {
    q: 'What if I make more or less than I told you?',
    a: 'Update your income anytime from the dashboard. The tax reserve recalculates immediately. We recommend a quick check-in monthly — especially during hail season when income can swing fast.',
  },
  {
    q: "What if I don't have the money to set aside?",
    a: "TruFlow tells you the truth about what you owe, even when it's uncomfortable. If you can't hit the full reserve, partial payments still reduce IRS penalties. Talk to a CPA about a payment plan if you're behind — knowing the number is the first step.",
  },
  {
    q: 'Will this help me find deductions?',
    a: "Not yet. TruFlow tracks expenses you enter, and those reduce your tax estimate. We're not built for aggressive deduction hunting — for that, you'll want a CPA or a dedicated deduction app at year-end.",
  },
  {
    q: 'Is my data safe?',
    a: "Passwords are hashed with bcrypt. All data encrypted in transit. We follow Gramm-Leach-Bliley financial privacy standards. We don't sell your data. Ever.",
  },
  {
    q: 'How does the pricing work?',
    a: `You get an intro rate for your first billing cycle — $${PRICING.monthly.intro}/month for your first ${PRICING.monthly.introMonths} months, or $${PRICING.annual.intro}/year for your first year. After that, it renews automatically at the standard rate: $${PRICING.monthly.base}/month or $${PRICING.annual.base}/year.`,
  },
  {
    q: 'What if I want a refund?',
    a: "We give a full refund within 30 days, no questions asked. Just email aden@gettruflow.com and I'll refund you directly. No automated forms, no runaround.",
  },
];

function FAQ() {
  const sectionRef = useSectionView('section_viewed_faq');
  const [openIndex, setOpenIndex] = useState(null);

  function toggle(i) {
    if (openIndex !== i) {
      posthog.capture('faq_opened', { question_index: i + 1, question_text: ITEMS[i].q });
    }
    setOpenIndex(prev => (prev === i ? null : i));
  }

  return (
    <section ref={sectionRef} style={{ backgroundColor: 'var(--paper)', padding: '96px 24px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '36px',
          color: 'var(--ink)',
          textAlign: 'center',
          margin: '0 0 48px 0',
        }}>
          Questions
        </h2>

        <div>
          {ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                style={{
                  borderBottom: '0.5px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <button
                  onClick={() => toggle(i)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 0',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    gap: '16px',
                  }}
                >
                  <span style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: 'var(--ink)',
                    lineHeight: 1.4,
                  }}>
                    {item.q}
                  </span>
                  <span style={{
                    flexShrink: 0,
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isOpen ? 'var(--signal)' : 'var(--signal-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.15s',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <line x1="6" y1="2" x2="6" y2="10" strokeWidth="1.5" strokeLinecap="round"
                        style={{ stroke: isOpen ? 'var(--on-signal)' : 'var(--signal)', display: isOpen ? 'none' : 'block' }} />
                      <line x1="2" y1="6" x2="10" y2="6" style={{ stroke: isOpen ? 'var(--on-signal)' : 'var(--signal)' }} strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>

                <div style={{
                  maxHeight: isOpen ? '400px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.28s ease',
                }}>
                  <p style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: '15px',
                    color: 'var(--slate)',
                    lineHeight: 1.7,
                    margin: '0 0 20px 0',
                    paddingRight: '40px',
                  }}>
                    {item.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default FAQ;
